import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCurrencyByCode, type Currency } from '@/lib/currencies';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';

export interface UserProfile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  currency: string | null;
  wallet_name: string | null;
  week_start: string | null;
  default_period: string | null;
  first_day_of_month: number | null;
  round_amounts: boolean | null;
  initial_balance: number | null;
  created_at: string;
  updated_at: string;
}

export type WeekStart = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
export type DefaultPeriod = 'week' | 'month' | 'quarter' | 'year';

export const weekStartOptions: { value: WeekStart; label: string }[] = [
  { value: 'monday', label: 'Понедельник' },
  { value: 'tuesday', label: 'Вторник' },
  { value: 'wednesday', label: 'Среда' },
  { value: 'thursday', label: 'Четверг' },
  { value: 'friday', label: 'Пятница' },
  { value: 'saturday', label: 'Суббота' },
  { value: 'sunday', label: 'Воскресенье' },
];

export const periodOptions: { value: DefaultPeriod; label: string }[] = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Год' },
];

// Shared auth state hook
export function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setIsReady(true);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, isReady };
}

export function useUserProfile() {
  const { user, isReady } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch profile with React Query - cached and deduplicated
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      
      return data as UserProfile | null;
    },
    enabled: !!user && isReady,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<UserProfile>) => {
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      toast({
        title: 'Сохранено',
        description: 'Настройки успешно обновлены',
      });
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось сохранить настройки',
        variant: 'destructive',
      });
    },
  });

  // Get current currency
  const currency: Currency = getCurrencyByCode(profile?.currency || 'KZT');

  // Helper to get label for week start
  const getWeekStartLabel = () => {
    const option = weekStartOptions.find(o => o.value === profile?.week_start);
    return option?.label || 'Понедельник';
  };

  // Helper to get label for default period
  const getPeriodLabel = () => {
    const option = periodOptions.find(o => o.value === profile?.default_period);
    return option?.label || 'Месяц';
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      await updateMutation.mutateAsync(updates);
      return true;
    } catch {
      return false;
    }
  };

  return {
    profile: profile ?? null,
    loading: !isReady || isLoading,
    user,
    currency,
    updateProfile,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['profile', user?.id] }),
    getWeekStartLabel,
    getPeriodLabel,
  };
}
